import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFavorites } from '@/contexts/FavoritesContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Star, MapPin, Clock, Heart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const { t, i18n } = useTranslation();
  const { favorites, removeFavorite } = useFavorites();
  const lang = i18n.language;

  const handleRemove = (doctorId, doctorName) => {
    removeFavorite(doctorId);
    toast.success(
      lang === 'ar' 
        ? `تمت إزالة ${doctorName} من المفضلة`
        : `${doctorName} removed from favorites`
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="favorites-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-red-500 to-red-600 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-8 w-8 fill-white" />
            <h1 className="text-3xl md:text-4xl font-bold">
              {lang === 'ar' ? 'الأطباء المفضلون' : 'Favorite Doctors'}
            </h1>
          </div>
          <p className="text-red-100">
            {lang === 'ar' 
              ? `لديك ${favorites.length} طبيب في قائمة المفضلة`
              : `You have ${favorites.length} doctors in your favorites`
            }
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              {lang === 'ar' ? 'لا يوجد أطباء مفضلون' : 'No favorite doctors yet'}
            </h2>
            <p className="text-slate-500 mb-6">
              {lang === 'ar' 
                ? 'ابدأ بإضافة الأطباء المفضلين لديك للوصول إليهم بسهولة'
                : 'Start adding your favorite doctors to easily access them later'
              }
            </p>
            <Button asChild className="bg-teal-600 hover:bg-teal-700">
              <Link to="/doctors">
                {lang === 'ar' ? 'تصفح الأطباء' : 'Browse Doctors'}
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((doctor) => (
              <Card 
                key={doctor.id} 
                className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300"
                data-testid={`favorite-card-${doctor.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <img
                      src={doctor.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=0F6D77&color=fff`}
                      alt={doctor.name}
                      className="h-20 w-20 rounded-xl object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{doctor.name}</h3>
                      <p className="text-sm text-teal-600 mb-2">{doctor.specialty?.name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium">{doctor.rating}</span>
                        <span className="text-slate-400">({doctor.reviews_count})</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-1 text-sm text-slate-500 mb-3">
                      <MapPin className="h-3.5 w-3.5" />
                      <span className="truncate">{doctor.address || 'N/A'}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" asChild>
                        <Link to={`/doctors/${doctor.id}`}>
                          {lang === 'ar' ? 'عرض' : 'View'}
                        </Link>
                      </Button>
                      <Button size="sm" className="flex-1 bg-teal-600 hover:bg-teal-700" asChild>
                        <Link to={`/doctors/${doctor.id}`}>
                          {lang === 'ar' ? 'احجز' : 'Book'}
                        </Link>
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRemove(doctor.id, doctor.name)}
                        data-testid={`remove-favorite-${doctor.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
